import { create } from 'zustand'
import { UsuarioInfo, AuthSessionResponse } from '@/types/auth'

/**
 * Auth store sem armazenamento de tokens.
 *
 * Tokens (access + refresh) ficam APENAS em cookies HttpOnly emitidos
 * pelo BFF — inacessíveis a JavaScript, eliminando o vetor de roubo
 * via XSS.
 *
 * O store mantém somente:
 *  • dados de identidade do usuário (para a UI exibir)
 *  • flag isAuthenticated (calculada a partir do hidrate /me ou login)
 *
 * Persistimos apenas o objeto `usuario` em sessionStorage (não em
 * localStorage) para manter a UI estável após reloads — sem expor
 * nenhum token. O cookie HttpOnly é a fonte da verdade da autenticação.
 */
export interface AuthStore {
  usuario: UsuarioInfo | null
  isAuthenticated: boolean
  isLoading: boolean
  error: string | null

  loginSuccess: (response: AuthSessionResponse) => void
  logout: () => void
  setLoading: (loading: boolean) => void
  setError: (error: string | null) => void
  setUsuario: (usuario: UsuarioInfo) => void
  hydrate: (usuario: UsuarioInfo | null) => void
}

const STORAGE_KEY = 'originei_user'

function readUsuario(): UsuarioInfo | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY)
    if (!raw || raw === 'undefined' || raw === 'null') return null
    return JSON.parse(raw) as UsuarioInfo
  } catch {
    sessionStorage.removeItem(STORAGE_KEY)
    return null
  }
}

function writeUsuario(u: UsuarioInfo | null) {
  if (u) sessionStorage.setItem(STORAGE_KEY, JSON.stringify(u))
  else sessionStorage.removeItem(STORAGE_KEY)
}

const initialUsuario = readUsuario()

export const authStore = create<AuthStore>((set) => ({
  usuario: initialUsuario,
  isAuthenticated: !!initialUsuario,
  isLoading: false,
  error: null,

  loginSuccess: (response: AuthSessionResponse) => {
    const usuario: UsuarioInfo = {
      id: response.usuarioId,
      nome: response.nome,
      email: response.email,
      permissoes: response.roles ?? [],
      ativo: true,
    }
    writeUsuario(usuario)
    set({ usuario, isAuthenticated: true, error: null })
  },

  logout: () => {
    writeUsuario(null)
    set({ usuario: null, isAuthenticated: false, error: null })
  },

  setLoading: (loading: boolean) => set({ isLoading: loading }),
  setError: (error: string | null) => set({ error }),

  setUsuario: (usuario: UsuarioInfo) => {
    writeUsuario(usuario)
    set({ usuario, isAuthenticated: true })
  },

  hydrate: (usuario: UsuarioInfo | null) => {
    writeUsuario(usuario)
    set({ usuario, isAuthenticated: !!usuario })
  },
}))
